import React, { useEffect, useMemo, useState } from 'react';
import bemCn from 'bem-cn';
import loadable from '@loadable/component';
import { seoFaqs } from '../../consts/seo-faqs.consts';
import { AUTH_TESTS } from '../listings/auth-test.const';
import { SIGN_UP_SRC } from '../../consts/profile.const';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { RouteFormatter } from '../../routes';
import { COOKIE_PARAM_NAME } from '../../consts/cookies.consts';
import { RenderTopCities, RenderTopState } from '../../components/seo-listings/top-list';
import RenderAppDownloadCity from '../../components/seo-listings/app-download';
import SeoCitySingleHeader from '../../components/seo-listings/header';
import RenderOtherTypes from '../listings/render-other-types';
import SpotBreadcrumbs from '../../components/spot-breadcrumbs';
import RenderHowWorks from '../../components/seo-listings/how-works';
import RenderWhyRent from '../../components/seo-listings/why-rent';
import ToogleButton from '../../components/toogle-button';
import NearbyCities from '../../components/seo-listings/nearby-cities';
import SniffButton from '../../components/sniff-button';
import SniffAsSeen from '../../components/sniif-as-seen';
import SpotDetails from '../../components/seo-listings/spot-details';
import SeoReviews from '../../components/seo-listings/reviews';
import SharePage from '../../components/spot-share-button/share-page';
import FooterNew from '../../components/footer-new';
import SeoFaqs from '../../components/seo-listings/faqs';
import SvgAmenitiesKiddiePool from 'src/assets/svg/amenities/kiddie-pool.svg';
import SvgAmenitiesIndoor from 'src/assets/svg/amenities/indoor.svg';
import SvgAmenitiesTrail from 'src/assets/svg/amenities/trail.svg';
import SvgTopBadgeColor from 'src/assets/svg/SvgTopBadgeColor.svg';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import SvgChevronLeft from 'src/assets/svg/SvgChevronLeft.svg';
import SvgFilterNew from 'src/assets/svg/SvgFilterNew.svg';
import SvgSearch from 'src/assets/svg/SvgSearch.svg';
import SvgFenced from 'src/assets/svg/SvgFenced.svg';
import SvgDogPaw from 'src/assets/svg/SvgDogPaw.svg';
import SvgChTree from 'src/assets/svg/SvgChTree.svg';
import SvgShare from 'src/assets/svg/SvgShare.svg';
import SvgRuler from 'src/assets/svg/SvgRuler.svg';
import SvgDog from 'src/assets/svg/SvgDog.svg';
import SvgPin from 'src/assets/svg/SvgPin.svg';
import './index.scss';

const CookieService = require('../../services/cookie.service');
const useGetSpotPagedBox = require('../../services/hooks/useGetSpotPagedBox');
const useGetSpotPhotosPaginated = require('../../services/hooks/useGetSpotPhotosPaginated');

const SniffAppleMaps = loadable(() => import('../../components/sniff-apple-maps/city'));
const BlurSignupModal = loadable(() => import('../../components/modal-blur-signup'));
const SkeletonSpotItem = loadable(() => import('../../components/skeleton/spot-item'));

const b = bemCn('seo-city-single');

const subSeoDesc =
    "Sniffspot's private dog parks are the best way to exercise your dog. We have the best variety and the best priced dog parks anywhere!";

const SPOT_PER_PAGE = 30;
const LISTING_DEFAULT_MAP_ZM = 25000; // in meters

const toggleFiltersBtn = [
    {
        id: 'PUBLIC_PARK',
        text: 'Public park',
        svg: <SvgChTree />,
        isOn: true,
        auth: AUTH_TESTS.TOGGLE_PUBL_SPOT,
    },
    {
        id: 'PRIVATE_PARK',
        text: 'Private park',
        svg: <SvgDogPaw />,
        isOn: true,
        auth: AUTH_TESTS.TOGGLE_PRIV_SPOT,
    },
    {
        id: 'FULLY_FENCED',
        text: 'Fully fenced',
        svg: <SvgFenced />,
        auth: AUTH_TESTS.TOGGLE_FENCED,
    },
    {
        id: 'WATER_PARKS',
        text: 'Water parks',
        svg: <SvgAmenitiesKiddiePool />,
        auth: AUTH_TESTS.TOGGLE_WATER,
    },
    {
        id: 'NO_DOGS',
        text: 'Fewer dogs seen/heard',
        svg: <SvgDog />,
        auth: AUTH_TESTS.TOGGLE_NODOGS,
    },
    {
        id: '10_MILES',
        text: '< 50 miles away',
        svg: <SvgPin />,
        auth: AUTH_TESTS.TOGGLE_IN10MILES,
        isOn: true,
    },
    {
        id: 'BIG_AREA',
        text: '0.5+ acres',
        svg: <SvgRuler />,
        auth: AUTH_TESTS.TOGGLE_MINHALFACR,
    },
    {
        id: 'TOP_SPOT',
        text: 'Top spots',
        svg: <SvgTopBadgeColor />,
        auth: AUTH_TESTS.TOGGLE_TOPSPOTS,
    },
    {
        id: 'HIKING',
        text: 'Hiking trails',
        svg: <SvgAmenitiesTrail />,
        auth: AUTH_TESTS.TOGGLE_HIKING,
    },
    {
        id: 'INDOOR',
        text: 'Indoor play space',
        svg: <SvgAmenitiesIndoor />,
        auth: AUTH_TESTS.TOGGLE_INDOOR,
    },
    {
        id: 'FILTER',
        text: 'More...',
        svg: <SvgFilterNew />,
        auth: AUTH_TESTS.TOGGLE_FILTERS,
    },
];

const seoListBread = (city) => [
    {
        name: 'All dog parks',
        url: RouteFormatter.listings({}),
    },
    {
        name: city?.state || '',
        url: RouteFormatter.listings({ first: city?.stateUrl }),
    },
    {
        name: city?.name || '',
        short: city?.stateShort || '',
    },
];

const TriggerComp = () => (
    <ToogleButton className={b('filter-toggle_button')} activeCssClass="active" style={{ padding: '6px 16px' }} svg={<SvgShare className="me-0" />} />
);

const RenderFilters = ({ getAuthTest, goToSearchPage, mobile }) => {
    const [shareModal, setShareModal] = useState(false);

    const toggleShareModal = () => setShareModal((s) => !s);

    return (
        <div className={b('filter-toggle').mix(mobile ? 'd-md-none' : '')}>
            <SharePage {...{ title: 'Share this page!', TriggerComp, shareModal, toggleShareModal }} />
            {toggleFiltersBtn.map((v) => (
                <ToogleButton
                    key={v.id}
                    className={b('filter-toggle_button')}
                    svg={v.svg}
                    name={v.text}
                    toogleOn={v.isOn}
                    onClick={v.auth ? () => getAuthTest(v.auth) : goToSearchPage}
                />
            ))}
        </div>
    );
};

const SeoAction = ({ city, exploreLink }) => (
    <div className="card-city action pt-4 pb-4">
        <h2 className="snif-m1 snif-l2-pc snif-semibold text-center mb-4 mt-md-3 mb-md-5">Rent safe and private dog parks</h2>
        <div className="text-center mb-md-3">
            <a href={exploreLink} rel="nofollow">
                <SniffButton size="lg" block="mobile">
                    {`Explore spots in ${city.name}, ${city.stateShort.toUpperCase()}`}
                </SniffButton>
            </a>
        </div>
    </div>
);

const Pagination = ({ page, pages, goToSearchPage }) => {
    const pageArray = [];

    if (pages <= 5) {
        for (let i = 1; i <= pages; i++) {
            pageArray.push(i);
        }
    } else {
        if (page <= 3) {
            pageArray.push(1, 2, 3, 4, 5);
        } else if (page >= pages - 2) {
            pageArray.push(pages - 4, pages - 3, pages - 2, pages - 1, pages);
        } else {
            pageArray.push(page - 2, page - 1, page, page + 1, page + 2);
        }
    }

    return (
        <div className={b('pagination')}>
            {page > 1 && (
                <span className={b('pagination_right')} onClick={() => goToSearchPage(page - 1)}>
                    <SvgChevronLeft />
                </span>
            )}
            {pageArray.map((i) => (
                <span key={i} className={b('pagination_number', { active: i == page })} onClick={() => goToSearchPage(i)}>
                    {i}
                </span>
            ))}
            {pages > 1 && (
                <span className={b('pagination_right')} onClick={() => goToSearchPage(page + 1)}>
                    <SvgChevronRight />
                </span>
            )}
        </div>
    );
};

const CitySingleTest = ({ city, serverSSR }) => {
    const isMobile = useMobileScreen();
    const [page, setPage] = useState(1);
    const [spots, setSpots] = useState(city.spots);
    const [showMap, setShowMap] = useState(false);
    const [isMapView, setisMapView] = useState(false);
    const [isBlurTest, setIsBlurTest] = useState(null);
    const [recalcZoom, setRecalcZoom] = useState(false);
    const [fullLoading, setFullLoading] = useState(false);
    const [activeSpotId, setActiveSpotId] = useState(null);
    const { getSpotPagedBox } = serverSSR ? {} : useGetSpotPagedBox.default();
    const { getSpotPhotosPaged } = serverSSR ? {} : useGetSpotPhotosPaginated.default();
    const cityText = `${city?.name}, ${city?.stateShort?.toUpperCase() || city?.state}`;
    const title = `Top Public & Private Dog Parks in ${cityText}`;
    const resultText = `${city.totalSpots} spot${city.totalSpots > 1 ? 's' : ''} found`;
    const pages = Math.ceil(city.totalSpots / SPOT_PER_PAGE);
    const showText = `Showing ${(page - 1) * SPOT_PER_PAGE + 1} - ${(page - 1) * SPOT_PER_PAGE + spots.length} of ${
        city.totalSpots > 100 ? '100+' : city.totalSpots
    } spots`;
    const myFaqs = useMemo(() => seoFaqs(city), [city]);

    const faqs = useMemo(
        () =>
            city?.faqs?.length > 0
                ? city.faqs.map((c) => ({
                      que: c.question,
                      ans: () => <p dangerouslySetInnerHTML={{ __html: c.answer }} />,
                  }))
                : myFaqs.filter((f) => f.show),
        [city]
    );

    const listBread = useMemo(() => seoListBread(city), [city]);

    const exploreLink = useMemo(
        () =>
            encodeURI(
                RouteFormatter.listings(
                    {},
                    {
                        query: {
                            loc: `${city.name}, ${city.stateShort.toUpperCase() || city.stateUrl}`,
                            lat: city.latitude || undefined,
                            lng: city.longitude || undefined,
                        },
                    }
                )
            ),
        [city]
    );

    const toggleMapView = () => setisMapView((m) => !m);

    const goToSearchPage = async (page) => {
        try {
            setFullLoading(true);
            const variables = {
                center: { lat: city.latitude, lng: city.longitude },
                filtersNew: {
                    distance: 50,
                    showPrivateSpots: true,
                    showPublicSpots: true,
                },
                page,
                limit: SPOT_PER_PAGE,
                hasPhotos: false,
            };

            setPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });

            const res = await getSpotPagedBox({ variables });

            if (res.data?.spotsPagedWithBox?.spots?.collection) {
                setRecalcZoom(true);
                setSpots(res.data.spotsPagedWithBox.spots.collection);
                setTimeout(() => {
                    setRecalcZoom(false);
                }, 1000);
            }
        } catch (error) {
            console.warn(error);
        }
        setFullLoading(false);
    };

    const getSpecificSpots = (item) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.location = item.link;
    };

    const getAuthTest = async (test) => {
        const re = `${window.location.pathname}#${test}`;
        CookieService.default.set(COOKIE_PARAM_NAME.REDIRECT, re);
        CookieService.default.set(COOKIE_PARAM_NAME.SIGN_UP_FROM, SIGN_UP_SRC.FILTERS_WEB, { expires: 1 });
        setIsBlurTest('v2');
        return true;
    };

    const filtersClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        getAuthTest(AUTH_TESTS.TOGGLE_FILTERS);
    };

    useEffect(() => {
        setShowMap(isMapView);
    }, [isMapView]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        if (!isMobile) {
            setShowMap(true);
        }
    }, []);

    return (
        <>
            <SeoCitySingleHeader {...{ city, isMapView, getAuthTest, toggleMapView }} />
            <RenderFilters {...{ getAuthTest, goToSearchPage }} mobile />
            <div className={b()}>
                <div className={b('main')}>
                    <div className={b('main-maps', { showMap })}>
                        <div className="d-none d-md-block">
                            <SpotBreadcrumbs {...{ listBread }} />
                            <div className="search-bar-closed" onClick={() => getAuthTest(AUTH_TESTS.TOGGLE_SEARCH)}>
                                <SvgSearch width={20} height={20} />
                                <div className="search-bar-closed_text-container">
                                    <div className="snif-s1">{cityText}</div>
                                    <div className="d-none d-md-block mx-1">/</div>
                                    <div className="snif-s2 snif-s1-pc">
                                        <span>Any date</span>•<span>Any time</span>
                                    </div>
                                </div>
                                <SniffButton className="filter-button" color="secondary" size="sm" onClick={filtersClick}>
                                    <SvgFilterNew width={20} height={20} />
                                    <div className="green-dot">1</div>
                                </SniffButton>
                            </div>
                        </div>
                        <div className={b('main-maps-content')}>
                            {showMap && (
                                <SniffAppleMaps
                                    center={{ latitude: parseFloat(city.latitude), longitude: parseFloat(city.longitude) }}
                                    spots={spots}
                                    cameraDistance={LISTING_DEFAULT_MAP_ZM}
                                    activeSpotId={activeSpotId}
                                    {...{ recalcZoom, setMapRect: () => {}, setMapCenter: () => {}, onGetSpotsList: () => {} }}
                                />
                            )}
                        </div>
                    </div>
                    <div className={b('main-content')}>
                        <div className="d-md-none">
                            <SpotBreadcrumbs {...{ listBread }} />
                            <div className="px-2">
                                <h2 className="snif-m1 snif-semibold text-dark mt-2 mb-1">{title}</h2>
                                <p className="snif-s1 snif-regular text-dark mb-3">{subSeoDesc}</p>
                                <div className={b('main-content-mobile-header_result')}>{resultText}</div>
                            </div>
                        </div>
                        <div className="d-none d-md-block position-relative my-3 ms-1">
                            <div className={b('main-content-desktop-header')}>
                                <h1 className="snif-l1 snif-semibold text-dark">{title}</h1>
                                <div className={b('main-content-desktop-header_result')}>{resultText}</div>
                            </div>
                            <RenderFilters {...{ getAuthTest, goToSearchPage }} />
                            <p className="snif-p snif-regular text-dark mt-3 mb-1">{subSeoDesc}</p>
                        </div>
                        <div className={b('listings')}>
                            {fullLoading
                                ? [...Array(15)].map((_, i) => <SkeletonSpotItem key={i} className="seo-spot-details" />)
                                : spots.map((spot, idx) => {
                                      const first = idx === 0;
                                      return (
                                          <SpotDetails
                                              key={spot.id}
                                              onMouseEnter={() => setActiveSpotId(spot.id)}
                                              onMouseLeave={() => setActiveSpotId(null)}
                                              {...{ spot, first, getSpotPhotosPaged }}
                                          />
                                      );
                                  })}
                        </div>
                        <div className={b('listings_footer')}>
                            <p className="d-none d-md-block snif-p snif-medium text-medium">{showText}</p>
                            <Pagination {...{ page, pages, goToSearchPage }} />
                        </div>
                    </div>
                </div>
                <div className="l-container">
                    <SniffAsSeen isMobileSSR={isMobile} useState={useState} />
                </div>
                <RenderWhyRent data={city} />
                <RenderOtherTypes
                    {...{
                        city,
                        subObj: null,
                        getSpecificSpots,
                        seoSubCityType: true,
                        isMobileSSR: isMobile,
                        SSR: true,
                        useState,
                        useEffect,
                    }}
                />
                <SeoReviews {...{ city, cityReviews: city.reviews, exploreLink }} />
                <RenderHowWorks {...{ isMobile, name: city?.name }} />
                <SeoFaqs {...{ city, faqs }} />
                <RenderAppDownloadCity />
                <NearbyCities {...{ city }} />
                <SeoAction {...{ city, exploreLink }} />
                <RenderTopCities SSR {...{ isMobile }} />
                <RenderTopState SSR {...{ isMobile }} />
            </div>
            <FooterNew {...{ isShowCities: false, isHome: true }} />
            <BlurSignupModal
                {...{
                    isBlurTest,
                    setIsBlurTest,
                    isMobile,
                    useStateSSR: useState,
                }}
            />
        </>
    );
};

export default CitySingleTest;
